"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Server, Network, CreditCard, Cpu, Smartphone, CircleCheck as CheckCircle2, ArrowRight, Sparkles, Layers, ShieldCheck, Zap } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
const serviceAreas = [{
  id: "pms",
  title: "PMS Selection & Implementation",
  shortTitle: "PMS Selection",
  badge: "Core Architecture",
  description: "Select, configure, and transition to Property Management Systems tailored to your property size, staff workflows, and revenue objectives.",
  benefit: "+35% Operational Efficiency",
  feature1: "Independent vendor evaluation & cost negotiation",
  feature2: "Zero-downtime guest & reservation data migration",
  feature3: "Standard operating procedure (SOP) configuration",
  feature4: "On-site and remote hotel staff training modules",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/pms_implementation.webp",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/pms_implementation.webp-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/pms_implementation.webp-wvc-sizes",
  imageAlt: "Hotel Property Management System dashboard interface"
}, {
  id: "channel",
  title: "Channel Manager Setup & Yielding",
  shortTitle: "Channel Manager",
  badge: "Distribution",
  description: "Connect your PMS to all major OTAs, GDS, and direct channels with automated rate distribution and real-time inventory synchronization.",
  benefit: "100% Rate & Inventory Parity",
  feature1: "Two-way API integration with 100+ booking channels",
  feature2: "Automated rate mapping & multiplier logic",
  feature3: "Real-time overbooking protection algorithms",
  feature4: "Channel yield performance and margin analytics",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/channel_manager.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/channel_manager.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/channel_manager.png-wvc-sizes",
  imageAlt: "Channel management synchronization portal"
}, {
  id: "booking",
  title: "Booking Engine Optimization",
  shortTitle: "Booking Engine",
  badge: "Direct Revenue",
  description: "Turn website visitors into high-value direct bookings with a friction-free, mobile-first booking experience and strategic upsells.",
  benefit: "+28% Direct Conversion Rate",
  feature1: "Custom checkout flow and speed optimization",
  feature2: "Integrated dynamic package & room upsells",
  feature3: "Multi-currency and local payment gateway setup",
  feature4: "Automated cart abandonment email recovery",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/booking_engine.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/booking_engine.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/booking_engine.png-wvc-sizes",
  imageAlt: "Mobile responsive direct booking engine"
}, {
  id: "integration",
  title: "Property Management Integration",
  shortTitle: "Tech Integration",
  badge: "Ecosystem Sync",
  description: "Unify isolated hotel systems into a connected stack bridging RMS, POS, CRM, digital key systems, and financial ledgers.",
  benefit: "Unified Real-Time Data Flow",
  feature1: "Custom API & webhook setup across tech partners",
  feature2: "Automated revenue management software (RMS) sync",
  feature3: "Unified single guest profile creation",
  feature4: "Instant billing and accounting ledger hooks",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/pms_integration.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/pms_integration.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/pms_integration.png-wvc-sizes",
  imageAlt: "Hotel tech ecosystem integration diagram"
}, {
  id: "guesttech",
  title: "Guest Experience Technology",
  shortTitle: "Guest Tech",
  badge: "Guest Satisfaction",
  description: "Deploy contactless web check-in, messaging concierges, and automated feedback loops to deliver modern hospitality while reducing front-desk load.",
  benefit: "4.8/5 Average Review Score",
  feature1: "Web check-in and digital key delivery setup",
  feature2: "Automated guest messaging via WhatsApp & SMS",
  feature3: "In-stay review generation and escalation routing",
  feature4: "Digital compendium and room service ordering",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/guest_experience.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/guest_experience.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/guest_experience.png-wvc-sizes",
  imageAlt: "Guest contactless check-in mobile web app"
}];
const highlights = [{
  title: "Vendor Agnostic Advice",
  description: "We recommend tools that fit your specific property size and strategy, not software that pays commissions.",
  icon: ShieldCheck
}, {
  title: "End-to-End Implementation",
  description: "From contract negotiation to initial configuration and staff onboarding, we manage the entire tech roll-out.",
  icon: Zap
}, {
  title: "Data Integrity & Continuity",
  description: "Zero lost reservations, full rate history preservation, and clean database setup during transitions.",
  icon: Layers
}];
export default function Technology() {
  const [activeTab, setActiveTab] = useState("pms");
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (id: string) => {
    switch (id) {
      case "pms":
        return <Server className="size-4 sm:size-5" />;
      case "channel":
        return <Network className="size-4 sm:size-5" />;
      case "booking":
        return <CreditCard className="size-4 sm:size-5" />;
      case "integration":
        return <Cpu className="size-4 sm:size-5" />;
      case "guesttech":
        return <Smartphone className="size-4 sm:size-5" />;
      default:
        return <Layers className="size-4 sm:size-5" />;
    }
  };
  return <section data-nav="light" id="technologyserviceareas" className="bg-background text-foreground py-16 sm:py-24 px-4 sm:px-6 lg:px-8 border-b border-border">
      <div className="max-w-7xl mx-auto space-y-12 sm:space-y-16">
        {}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <Badge variant="outline" className="border-primary/40 bg-accent text-accent-foreground px-3 py-1 text-xs font-semibold uppercase tracking-widest rounded-full">
            Hospitality Tech Architecture
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-foreground tracking-tight">
            Technology Service Areas
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Eliminate tech friction and maximize revenue potential with expert recommendation, setup, and seamless integration across your entire hotel software stack.
          </p>
        </div>

        {}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-8">
          {}
          <div className="flex justify-center">
            <TabsList className="flex h-auto w-full max-w-5xl flex-wrap justify-center gap-1.5 sm:gap-2 bg-muted/60 p-2 rounded-xl border border-border shadow-xs">
              {serviceAreas.map((area, i) => <TabsTrigger key={area.id} value={area.id} data-index={i} className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition-all data-[state=active]:bg-secondary data-[state=active]:text-secondary-foreground data-[state=active]:shadow-md hover:text-foreground">
                  {getIcon(area.id)}
                  <span>{area.shortTitle}</span>
                </TabsTrigger>)}
            </TabsList>
          </div>

          {}
          {serviceAreas.map((area, i) => <TabsContent key={area.id} value={area.id} data-index={i} className="mt-6 outline-none">
              <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            ease: "easeOut"
          }}>
                <Card className="border border-border bg-card shadow-xl overflow-hidden rounded-2xl">
                  <CardContent className="p-0">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
                      {}
                      <div className="lg:col-span-7 p-6 sm:p-8 lg:p-10 flex flex-col justify-between space-y-8">
                        <div className="space-y-6">
                          <div className="flex flex-wrap items-center gap-3">
                            <Badge variant="outline" className="border-primary/40 bg-accent text-accent-foreground font-semibold px-3 py-1 text-xs">
                              {area.badge}
                            </Badge>
                            <span className="inline-flex items-center gap-1.5 text-xs font-bold text-primary uppercase tracking-wider bg-primary/10 px-3 py-1 rounded-full">
                              <Sparkles className="size-3.5" />
                              {area.benefit}
                            </span>
                          </div>

                          <div>
                            <h3 className="text-2xl sm:text-3xl font-bold text-card-foreground tracking-tight mb-3">
                              {area.title}
                            </h3>
                            <p className="text-muted-foreground text-base leading-relaxed">
                              {area.description}
                            </p>
                          </div>

                          {}
                          <div className="space-y-3 pt-2">
                            <h4 className="text-xs font-bold text-foreground uppercase tracking-widest">
                              Key Capabilities & Scope:
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div className="flex items-start gap-2.5 bg-muted/40 p-3 rounded-lg border border-border/50">
                                <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                                <span className="text-xs sm:text-sm font-medium text-foreground">
                                  {area.feature1}
                                </span>
                              </div>
                              <div className="flex items-start gap-2.5 bg-muted/40 p-3 rounded-lg border border-border/50">
                                <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                                <span className="text-xs sm:text-sm font-medium text-foreground">
                                  {area.feature2}
                                </span>
                              </div>
                              <div className="flex items-start gap-2.5 bg-muted/40 p-3 rounded-lg border border-border/50">
                                <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                                <span className="text-xs sm:text-sm font-medium text-foreground">
                                  {area.feature3}
                                </span>
                              </div>
                              <div className="flex items-start gap-2.5 bg-muted/40 p-3 rounded-lg border border-border/50">
                                <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                                <span className="text-xs sm:text-sm font-medium text-foreground">
                                  {area.feature4}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {}
                        <div className="flex flex-wrap items-center gap-4 pt-6 border-t border-border">
                          <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-md">
                            <Link to="/ContactPage">
                              <span>Schedule a Tech Audit</span>
                              <ArrowRight className="size-4 ml-1" />
                            </Link>
                          </Button>
                          <Button asChild variant="outline" size="lg" className="border-border hover:bg-accent text-foreground font-semibold">
                            <Link to="/ServicesPage">
                              <span>View All Consulting Services</span>
                            </Link>
                          </Button>
                        </div>
                      </div>

                      {}
                      <div className="lg:col-span-5 bg-secondary relative overflow-hidden flex items-center justify-center p-6 lg:p-8 min-h-[300px]">
                        <div className="relative w-full h-full flex flex-col justify-between">
                          <div className="relative rounded-xl overflow-hidden shadow-2xl border border-border/20 group w-full">
                            <img src={area.image} data-wvc-srcset={area.imageSrcset} data-wvc-sizes={area.imageSizes} alt={area.imageAlt} loading="lazy" decoding="async" className="w-full h-64 sm:h-72 lg:h-80 object-cover object-center transition-transform duration-500 group-hover:scale-105" />
                            <div className="absolute inset-0 bg-gradient-to-t from-secondary/90 via-secondary/20 to-transparent" />
                            <div className="absolute bottom-4 left-4 right-4 p-4 rounded-lg bg-secondary/90 backdrop-blur-md border border-border/20 shadow-lg">
                              <p className="text-xs text-primary font-bold uppercase tracking-wider mb-0.5">
                                Typical Result
                              </p>
                              <p className="text-sm text-secondary-foreground font-extrabold">
                                {area.benefit}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </TabsContent>)}
        </Tabs>

        {}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6">
          {highlights.map((item, i) => {
          const IconComp = item.icon;
          return <div key={item.title} data-index={i} className="bg-card p-6 rounded-xl border border-border shadow-xs flex flex-col justify-between space-y-3">
                <div className="space-y-3">
                  <div className="size-10 rounded-lg bg-accent text-accent-foreground flex items-center justify-center border border-primary/20">
                    <IconComp className="size-5 text-primary" />
                  </div>
                  <h4 className="text-lg font-bold text-card-foreground">
                    {item.title}
                  </h4>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>;
        })}
        </div>

        {}
        <div className="bg-secondary text-secondary-foreground rounded-2xl p-6 sm:p-8 lg:p-10 flex flex-col sm:flex-row items-center justify-between gap-6 shadow-xl border border-border/20">
          <div className="space-y-2 text-center sm:text-left">
            <h3 className="text-xl sm:text-2xl font-bold text-secondary-foreground">
              Ready to Upgrade Your Hotel Tech Stack?
            </h3>
            <p className="text-secondary-foreground/80 text-sm sm:text-base max-w-xl">
              Book a consultation with Sunil Singh to review your PMS, channel manager, and booking engine setups for maximum revenue output.
            </p>
          </div>
          <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-6 py-3 whitespace-nowrap shrink-0 shadow-md">
            <Link to="/ContactPage">
              <span>Book a Tech Consultation</span>
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}